import type { ProDetailsViewModelFactory } from '@/features/iam'
import { type Member } from '@/features/iam/members/Member'
import type { ProDetailsViewModel } from '@/features/iam/providers/ProDetails/ProDetailsViewModel'
import { type UserDataStore } from '@/features/iam/users/UserDataStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { Storage } from '@/infra/storage'
import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'

const REVIEWED_KEY = 'software-stack-reviewed'

export class ProSoftwareReviewModalViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor details: ProDetailsViewModel | null = null

  constructor(
    private readonly _createProDetailsViewModel: PromiseLike<ProDetailsViewModelFactory>,
    private readonly _userDataStore: UserDataStore,
    private readonly _localStorage: Storage,
  ) {}

  /**
   * Offers the one-time software review when a pro lands after login.
   *
   * @param member
   */
  async maybeShow(member: Member) {
    if (this.modal.showing || this._localStorage.getItem(REVIEWED_KEY)) {
      return
    }

    // The review is optional, so a failed load just skips it until the next
    // member change.
    await this._showForPro(member).catch(() => undefined)
  }

  private async _showForPro(member: Member) {
    const publicId = member.user.publicId
    await this._userDataStore.fetchByPublicId(publicId)
    const isPro =
      this._userDataStore.getByPublicId(publicId)?.userDataType === 'Pro'
    if (!isPro) {
      return
    }

    const createProDetailsViewModel = await this._createProDetailsViewModel
    const details = createProDetailsViewModel(publicId, true)
    await details.fetch()
    runInAction(() => {
      this.details = details
      this.modal.show()
    })
  }

  confirm = task.resolved(async () => {
    if (!this.details) {
      return
    }

    const saved = await this.details.save()
    if (saved) {
      this._localStorage.setItem(REVIEWED_KEY, 'true')
      this.modal.close()
    }
  })
}
